// HR's inbox of Talk to HR requests: views on the left, the list in the middle, the open request
// on the right. Status follows who spoke last (the API moves it): HR's reply waits on the
// employee, their reply brings it back to Needs reply. Whoever first replies to the employee owns
// the request (or takes it); it can be given to anyone in Ask HR - Managers or Ask HR - Agents.
// Internal notes and the owner stay with HR.

import { useEffect, useRef, useState } from "react";
import { CATEGORIES, STATUS_LABELS, overdueReason, type Escalation, type EscalationStatus, type Owner } from "@ask-hr/core/escalations";
import { api } from "../api.js";
import { AttachPicker, AttachmentLinks, uploadAll } from "../attachments.js";
import { navigate } from "../router.js";
import { VIEWS, age, eventLines, first, inView, initials, lastActivity, matches, needsReply, overdue, overdueChips, snippetOf, sortFor, titleOf, waitingSince, type View } from "./requests.js";

const PILL: Record<EscalationStatus, string> = { new: "pill alert", in_progress: "pill", waiting: "pill wait", resolved: "pill ok" };
const when = (iso: string) => new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
const LIST = "/admin/requests";

function Lock() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="lock-icon">
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

export function RequestsPage({ openId }: { openId: string | null }) {
  const [requests, setRequests] = useState<Escalation[] | null>(null);
  const [me, setMe] = useState("");
  const [owners, setOwners] = useState<Owner[]>([]);
  const [error, setError] = useState(false);
  const [view, setView] = useState<View>("open");
  const [query, setQuery] = useState("");

  const fetchAll = () => api<{ requests: Escalation[]; me: string }>("GET", "/api/admin/escalations");
  const reload = () =>
    fetchAll().then(
      (r) => {
        setRequests(r.requests);
        setMe(r.me);
      },
      () => setError(true),
    );

  useEffect(() => {
    let live = true;
    fetchAll().then(
      (r) => {
        if (!live) return;
        setRequests(r.requests);
        setMe(r.me);
      },
      () => live && setError(true),
    );
    // Without owners the inbox still works; the Owner menu just offers none.
    api<{ owners: Owner[] }>("GET", "/api/admin/escalations/owners").then(
      (r) => live && setOwners(r.owners),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, []);

  const all = requests ?? [];
  const now = new Date();
  const opened = openId ? all.find((r) => r.id === openId) : undefined;
  const shown = all.filter((r) => inView(r, view, me, now) && matches(r, query)).sort(sortFor(view));
  const count = (v: View) => all.filter((r) => inView(r, v, me, now)).length;

  function showOthers(r: Escalation) {
    setView("all");
    setQuery(r.requester.email);
  }

  return (
    <section aria-labelledby="req-h">
      <h2 id="req-h" className="sr-only">
        Talk to HR requests
      </h2>
      {error ? (
        <p className="notice" role="alert">
          Couldn't load requests. Try again.
        </p>
      ) : null}
      <div className={`desk${openId ? " open" : ""}`}>
        <nav className="rail" aria-label="Request views">
          <h3>Views</h3>
          {VIEWS.map(([v, label]) => {
            const n = count(v);
            return (
              <button
                key={v}
                type="button"
                aria-pressed={view === v}
                className={[v === "needs" || v === "resolved" ? "gap" : "", (v === "needs" || v === "overdue") && n ? (v === "overdue" ? "hot late" : "hot") : ""].join(" ").trim() || undefined}
                onClick={() => {
                  // A new view starts with nothing open, until a request in it is picked.
                  setView(v);
                  if (openId) navigate(LIST);
                }}
              >
                {label}
                <span className="n">{requests ? n : ""}</span>
              </button>
            );
          })}
          <p className="help">Requests come in from Talk to HR on the web and in Slack. hr@novara.com gets a link to each new one.</p>
        </nav>

        <section className="list" aria-label="Requests">
          <div className="lhead">
            <label>
              <span className="sr-only">Search requests</span>
              <input type="search" placeholder="Search name, #number or text" value={query} onChange={(e) => setQuery(e.target.value)} />
            </label>
            <div className="small muted lmeta">
              <span>{requests ? `${shown.length} request${shown.length === 1 ? "" : "s"}` : "Loading…"}</span>
              <span>{view === "needs" ? "Waiting longest first" : view === "open" ? "Needs reply first" : view === "overdue" ? "Oldest first" : "Latest activity first"}</span>
            </div>
          </div>
          <div className="rows">
            {requests && !shown.length ? (
              <p className="blank">{query.trim() ? `Nothing matches "${query.trim()}".` : view === "needs" ? "Nobody is waiting on HR." : view === "open" ? "No open requests." : view === "overdue" ? "Nothing is overdue." : "None."}</p>
            ) : null}
            {shown.map((r) => {
              const waiting = needsReply(r);
              const since = waiting ? waitingSince(r) : lastActivity(r);
              const due = overdue(r, now);
              const chips = overdueChips(due);
              return (
                <button key={r.id} type="button" className="row" aria-current={r.id === openId} onClick={() => navigate(`${LIST}/${r.id}`)}>
                  {waiting ? <span className="dot" title="Needs reply" /> : null}
                  <span className="r1">
                    <span className="nm">{r.requester.name}</span>
                    {r.number ? <span className="num">#{r.number}</span> : null}
                    <span className={due.reply ? "age late" : "age"} title={waiting ? `Waiting for HR since ${when(since)}` : `Last activity ${when(since)}`}>
                      {age(since, now)}
                    </span>
                  </span>
                  <span className="small">{CATEGORIES[r.category]}</span>
                  <span className="snip">{snippetOf(r)}</span>
                  <span className="r4">
                    <span className={PILL[r.status]}>{STATUS_LABELS[r.status]}</span>
                    {chips.map((c) => (
                      <span key={c} className="flag" title={overdueReason(due)}>
                        {c}
                      </span>
                    ))}
                    {r.assignee ? (
                      <>
                        <span className="av" aria-hidden="true">
                          {initials(r.assignee.name)}
                        </span>
                        {r.assignee.oid === me ? "You" : first(r.assignee.name)}
                      </>
                    ) : (
                      <>
                        <span className="av none" aria-hidden="true" />
                        No owner
                      </>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <section className="detail" aria-label="Open request">
          {opened ? (
            <RequestDetail
              key={opened.id}
              request={opened}
              owners={owners}
              me={me}
              others={all.filter((x) => x.requester.oid === opened.requester.oid && x.id !== opened.id).length}
              onChanged={() => void reload()}
              onOthers={() => showOthers(opened)}
            />
          ) : (
            <p className="blank">{openId && requests ? "That request couldn't be found." : "Pick a request."}</p>
          )}
        </section>
      </div>
    </section>
  );
}

function RequestDetail({
  request: r,
  owners,
  me,
  others,
  onChanged,
  onOthers,
}: {
  request: Escalation;
  owners: Owner[];
  me: string;
  others: number;
  onChanged: () => void;
  onOthers: () => void;
}) {
  const [mode, setMode] = useState<"reply" | "note">("reply");
  const [text, setText] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const thread = useRef<HTMLDivElement>(null);
  const name = first(r.requester.name);
  const hasContent = Boolean(text.trim() || files.length);
  const reply = mode === "reply";
  // The current owner stays in the menu even if they've left the owner groups.
  const ownerChoices = r.assignee && !owners.some((o) => o.oid === r.assignee?.oid) ? [...owners, r.assignee] : owners;

  useEffect(() => {
    thread.current?.scrollTo({ top: thread.current.scrollHeight });
  }, [r.history.length]);

  async function patch(body: Record<string, unknown>, withText = false) {
    setBusy(true);
    setProblem(null);
    try {
      const attachments = withText && files.length ? await uploadAll(`/api/admin/escalations/${r.id}`, files) : [];
      await api("PATCH", `/api/admin/escalations/${r.id}`, {
        ...body,
        ...(withText ? { note: text, toEmployee: reply } : {}),
        ...(attachments.length ? { attachments } : {}),
      });
      if (withText) {
        setText("");
        setFiles([]);
      }
      onChanged();
    } catch (e) {
      setProblem(e instanceof Error && e.message ? e.message : "That didn't save. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="dhead">
        <div className="t">
          <button type="button" className="back linkbtn" onClick={() => navigate(LIST)}>
            ← All requests
          </button>
          <span className="num">
            {r.number ? `#${r.number} · ` : ""}
            {CATEGORIES[r.category]}
          </span>
          <h3>{titleOf(r)}</h3>
        </div>
        <div className="ctrls">
          <label>
            Status
            <select value={r.status} disabled={busy} onChange={(e) => void patch({ status: e.target.value })}>
              {(Object.keys(STATUS_LABELS) as EscalationStatus[]).map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </label>
          <label>
            Owner
            <select value={r.assignee?.oid ?? ""} disabled={busy} onChange={(e) => void patch({ assignee: e.target.value || null })}>
              <option value="">No owner</option>
              {ownerChoices.map((o) => (
                <option key={o.oid} value={o.oid}>
                  {o.oid === me ? `${o.name} (you)` : o.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>
      {overdueReason(overdue(r, new Date())) ? <div className="overdue-line">Overdue: {overdueReason(overdue(r, new Date()))}</div> : null}
      <div className="facts">
        <span>
          <b>{r.requester.name}</b>
        </span>
        {/* Plain text, not a mailto link: answers go on the request, where they're tracked. */}
        <span>{r.requester.email}</span>
        <span>{r.location}</span>
        <span>Came in on {r.channel === "slack" ? "Slack" : "the web"}</span>
        {r.emailed ? null : <span>Email to HR not sent</span>}
        {others ? (
          <button type="button" className="linkbtn" onClick={onOthers}>
            {others} other request{others === 1 ? "" : "s"}
          </button>
        ) : null}
      </div>
      {r.assignee || r.status === "resolved" ? null : (
        <div className="take">
          <span>No one owns this yet. Replying takes it for you.</span>
          <button type="button" disabled={busy} onClick={() => void patch({ assignee: me })}>
            Take it
          </button>
        </div>
      )}
      <div className="thread" ref={thread}>
        <div className="rmsg them">
          <div className="who">
            <b>{r.requester.name}</b>
            <span>asked HR · {when(r.createdAt)}</span>
          </div>
          {r.note ? <p>{r.note}</p> : <p className="muted">No note. They shared their Ask HR conversation.</p>}
          {r.excerpt.length ? (
            <details>
              <summary>Their Ask HR conversation ({r.excerpt.length} messages)</summary>
              <div className="ex">
                {r.excerpt.map((t, i) => (
                  <p key={i}>
                    <b>{t.role === "user" ? name : "Ask HR"}:</b> {t.text}
                  </p>
                ))}
              </div>
            </details>
          ) : null}
        </div>
        {r.history.map((h, i) => {
          const lines = eventLines(h, i).map((l, j) => (
            <div key={`s${i}-${j}`} className="sys">
              {l} · {when(h.at)}
            </div>
          ));
          if (!h.note && !h.attachments?.length) return lines;
          const kind = h.author === "employee" ? "them" : h.visibility === "employee" ? "us" : "note";
          return [
            <div key={`m${i}`} className={`rmsg ${kind}`}>
              <div className="who">
                {kind === "note" ? (
                  <span className="lock">
                    <Lock />
                    Internal note
                  </span>
                ) : (
                  <b>{h.by}</b>
                )}
                <span>
                  {kind === "note" ? `${h.by} · ` : kind === "us" ? `to ${name} · ` : ""}
                  {when(h.at)}
                  {kind === "note" ? ` · ${name} can't see this` : ""}
                </span>
              </div>
              {h.note ? <p>{h.note}</p> : null}
              {h.attachments?.length ? (
                <AttachmentLinks items={h.attachments} path={(a) => `/api/admin/escalations/${r.id}/attachments/${a.id}`} />
              ) : null}
            </div>,
            ...lines,
          ];
        })}
        {r.hiddenAt ? (
          <div className="sys">
            {name} removed this from their list in Ask HR on {when(r.hiddenAt)}. They won't see new replies.
          </div>
        ) : null}
      </div>
      <div className={`reply-box ${mode}`}>
        <div className="ctabs" role="tablist" aria-label="Write">
          <button type="button" role="tab" aria-selected={reply} onClick={() => setMode("reply")}>
            Reply to {name}
          </button>
          <button type="button" role="tab" aria-selected={!reply} onClick={() => setMode("note")}>
            Internal note
          </button>
        </div>
        <textarea
          aria-label={reply ? `Reply to ${name}` : "Internal note"}
          rows={3}
          maxLength={2000}
          placeholder={reply ? `Write to ${name}…` : "Only HR sees this…"}
          value={text}
          disabled={busy}
          onChange={(e) => setText(e.target.value)}
        />
        <AttachPicker files={files} onChange={setFiles} onProblem={setProblem} disabled={busy} />
        {problem ? (
          <p className="notice" role="alert">
            {problem}
          </p>
        ) : null}
        <div className="cfoot">
          <span className="hint">
            {reply ? (
              `${name} sees this in Ask HR and gets an email and a Slack message with a link.${r.status === "resolved" ? "" : " The request moves to Waiting on employee."}`
            ) : (
              <span className="lock">
                <Lock />
                Only HR sees this. {name} isn't told.
              </span>
            )}
          </span>
          {reply ? (
            <>
              <button type="button" disabled={busy || !hasContent} onClick={() => void patch({}, true)}>
                Send to {name}
              </button>
              <button type="button" className="primary" disabled={busy || !hasContent} onClick={() => void patch({ status: "resolved" }, true)}>
                Send and resolve
              </button>
            </>
          ) : (
            <button type="button" className="primary" disabled={busy || !hasContent} onClick={() => void patch({}, true)}>
              Save note
            </button>
          )}
        </div>
      </div>
    </>
  );
}
